import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../hooks/useTheme';
import { Text } from '../atoms/Typography/Text';
import { formatMeasuredAt } from '../../utils/measurementDate';
import { seriesColourVar, type SeriesColourSlot } from './parameterColours';
import {
  entryForDay,
  niceDomain,
  readCssVar,
  sharedTimeline,
  timeDomain,
  usePrefersReducedMotion,
  valuesOnTimeline,
  type ChartSeries,
  type TimelinePoint,
} from './historyChartUtils';
import { formatDate, formatNumber, sourceLabelKey } from './measurementHistoryUtils';
import './MeasurementTrendChart.css';

export interface ChartColors {
  series: string;
  band: string;
  bandEdge: string;
  grid: string;
  axis: string;
  surface: string;
  /** Intervention markers (#1824). */
  marker: string;
}

function resolveColors(slot: SeriesColourSlot | null): ChartColors {
  return {
    series: (slot ? readCssVar(seriesColourVar(slot)) : '') || readCssVar('--color-primary'),
    band: readCssVar('--color-status-successSubtle'),
    bandEdge: readCssVar('--color-status-success'),
    grid: readCssVar('--color-border-secondary'),
    axis: readCssVar('--color-text-muted'),
    surface: readCssVar('--color-background-surface'),
    marker: readCssVar('--color-status-info'),
  };
}

/** Colours read from the CSS custom properties (tokens) — re-read when the theme changes. */
export function useChartColors(slot: SeriesColourSlot | null): ChartColors {
  const { theme } = useTheme();
  const [colors, setColors] = useState<ChartColors>(() => resolveColors(slot));
  useEffect(() => {
    setColors(resolveColors(slot));
  }, [slot, theme]);
  return colors;
}

/** Re-reads one token per slot when the theme changes (combined chart: one colour per series). */
export function useSeriesColors(slots: readonly SeriesColourSlot[]): string[] {
  const { theme } = useTheme();
  const key = slots.join(',');
  const [colors, setColors] = useState<string[]>(() => slots.map((s) => readCssVar(seriesColourVar(s))));
  useEffect(() => {
    setColors(key ? key.split(',').map((s) => readCssVar(seriesColourVar(s as SeriesColourSlot))) : []);
  }, [key, theme]);
  return colors;
}

interface ChartRow {
  time: number;
  value: number | null;
  /** Controller probe (#1899): window average and [min, max] band. */
  probe?: number | null;
  probeBand?: [number, number] | null;
}

/** Hourly (or daily) controller probe readings shown behind the test points. */
export interface ProbeLine {
  /** "GHL ProfiLux" or similar, for the tooltip. */
  readonly label: string;
  readonly unit: string;
  /** Oldest first. */
  readonly points: ReadonlyArray<{ readonly time: number; readonly value: number; readonly min: number | null; readonly max: number | null }>;
}

/** The hovered calendar day, as a date without time. */
export function formatDay(at: TimelinePoint, language: string): string {
  return formatMeasuredAt({ measuredAt: new Date(at.time).toISOString(), measuredDate: at.day }, language);
}

export interface TrendTooltipContentProps {
  series: ChartSeries;
  /** The hovered (or synchronised) position of the shared time axis. */
  at: TimelinePoint;
}

/**
 * Tooltip of one chart for a hovered date: this parameter's value on that day
 * (exact time first, else the same day), or "no value on this day" — the
 * charts of the other selected parameters show theirs at the same moment.
 */
export function TrendTooltipContent({ series, at }: TrendTooltipContentProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const entry = entryForDay(series.points, at);
  return (
    <div className="measurement-trend-chart__tooltip" data-testid={`trend-tooltip-${series.parameterId}`}>
      <Text as="span" size="xs" weight="semibold">
        {series.label}
      </Text>
      {entry ? (
        <>
          <Text as="span" size="sm" weight="semibold">
            {`${formatNumber(entry.value, language)} ${series.unit}`.trim()}
          </Text>
          <Text as="span" size="xs" color="secondary">
            {formatMeasuredAt(entry, language)}
          </Text>
          <Text as="span" size="xs" color="secondary">
            {t(sourceLabelKey(entry.source))}
          </Text>
        </>
      ) : (
        <Text as="span" size="xs" color="secondary">
          {t('measurement.history.chart.noValueOnDay', { date: formatDay(at, language) })}
        </Text>
      )}
    </div>
  );
}

/** Tooltip on a probe reading (no test value at that time). */
function ProbeTooltipContent({ probe, reading }: { probe: ProbeLine; reading: ProbeLine['points'][number] }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  return (
    <div className="measurement-trend-chart__tooltip" data-testid="trend-tooltip-probe">
      <Text as="span" size="xs" weight="semibold">
        {t('controller.probe.tooltipTitle', { origin: probe.label })}
      </Text>
      <Text as="span" size="sm" weight="semibold">
        {`${formatNumber(reading.value, language)} ${probe.unit}`.trim()}
      </Text>
      {reading.min !== null && reading.max !== null && (
        <Text as="span" size="xs" color="secondary">
          {t('controller.probe.window', { min: formatNumber(reading.min, language), max: formatNumber(reading.max, language) })}
        </Text>
      )}
      <Text as="span" size="xs" color="secondary">
        {new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(reading.time))}
      </Text>
    </div>
  );
}

export interface MeasurementTrendChartProps {
  series: ChartSeries;
  /**
   * Shared time axis of all charts of the view (sharedTimeline of every
   * selected series). Defaults to this series' own values.
   */
  timeline?: readonly TimelinePoint[];
  /** Charts with the same syncId show their tooltip / crosshair at the same date. */
  syncId?: string;
  /** Text summary of the series, rendered as the figure caption. */
  caption: ReactNode;
  /** Plot height in px. */
  height?: number;
  /**
   * Vertical markers (Reef Guard #1824 interventions) at these times. Visual
   * only — the page lists them as text next to the charts.
   */
  markers?: ReadonlyArray<{ readonly time: number; readonly key: string }>;
  /** Controller probe line (dashed, with its min–max band) behind the test values. */
  probe?: ProbeLine;
  /** Shared x range of all charts of the view (tests and probes); defaults to this chart's timeline. */
  domain?: [number, number];
}

/**
 * Trend line of one parameter with its own y axis (unit) and target band.
 * Several charts share one time axis and range and synchronise their tooltip
 * by value (#1653). Visual only: the caption carries the text summary and the
 * history table is the accessible, keyboard-operable equivalent of every
 * point, so the SVG is hidden from assistive technology.
 */
export function MeasurementTrendChart({ series, timeline, syncId, caption, height = 240, markers = [], probe, domain: domainOverride }: MeasurementTrendChartProps) {
  const { i18n } = useTranslation();
  const language = i18n.language || 'de';
  const colors = useChartColors(series.slot);
  const reducedMotion = usePrefersReducedMotion();
  const target = series.target;

  const axis = useMemo(() => timeline ?? sharedTimeline([series]), [timeline, series]);
  const dayByTime = useMemo(() => new Map(axis.map((p) => [p.time, p])), [axis]);
  const data: ChartRow[] = useMemo(() => {
    const values = valuesOnTimeline(series.points, axis);
    const rows = new Map<number, ChartRow>(axis.map((p, i) => [p.time, { time: p.time, value: values[i] ?? null }]));
    // Probe readings get rows of their own times (no test value there); both lines connect across gaps.
    for (const p of probe?.points ?? []) {
      const row = rows.get(p.time) ?? { time: p.time, value: null };
      row.probe = p.value;
      row.probeBand = p.min !== null && p.max !== null ? [p.min, p.max] : null;
      rows.set(p.time, row);
    }
    return [...rows.values()].sort((a, b) => a.time - b.time);
  }, [series.points, axis, probe]);
  const probeByTime = useMemo(() => new Map((probe?.points ?? []).map((p) => [p.time, p])), [probe]);
  const domain = domainOverride ?? timeDomain(axis);
  const visibleMarkers = domain ? markers.filter((m) => m.time >= domain[0] && m.time <= domain[1]) : [];

  const yScale = useMemo(() => {
    const values = series.points.map((p) => p.value);
    for (const p of probe?.points ?? []) values.push(p.value, p.min ?? p.value, p.max ?? p.value);
    if (target) values.push(target.min, target.max);
    return niceDomain(Math.min(...values), Math.max(...values));
  }, [series.points, target, probe]);

  const renderTooltip = ({ active, label }: TooltipProps<number, string>) => {
    if (!active || typeof label !== 'number') return null;
    const at = dayByTime.get(label);
    if (at) return <TrendTooltipContent series={series} at={at} />;
    const reading = probeByTime.get(label);
    return reading && probe ? <ProbeTooltipContent probe={probe} reading={reading} /> : null;
  };

  return (
    <figure className="measurement-trend-chart">
      <figcaption className="measurement-trend-chart__caption">{caption}</figcaption>
      <div
        className="measurement-trend-chart__plot"
        aria-hidden="true"
        data-testid="measurement-trend-chart"
        data-parameter={series.parameterId}
        data-sync-id={syncId}
        data-animated={reducedMotion ? 'false' : 'true'}
      >
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }} syncId={syncId} syncMethod="value">
            <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
            {target && (
              <ReferenceArea
                y1={target.min}
                y2={target.max}
                fill={colors.band}
                fillOpacity={1}
                stroke={colors.bandEdge}
                strokeOpacity={0.4}
                ifOverflow="extendDomain"
              />
            )}
            {visibleMarkers.map((m) => (
              <ReferenceLine key={m.key} x={m.time} stroke={colors.marker} strokeDasharray="4 4" strokeWidth={2} data-testid="trend-marker" />
            ))}
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={domain ?? ['dataMin', 'dataMax']}
              tickFormatter={(value: number) => formatDate(value, language)}
              stroke={colors.axis}
              tick={{ fill: colors.axis, fontSize: 12 }}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              domain={yScale.domain}
              ticks={yScale.ticks}
              tickFormatter={(value: number) => formatNumber(value, language)}
              stroke={colors.axis}
              tick={{ fill: colors.axis, fontSize: 12 }}
              tickLine={false}
              width={64}
              label={
                series.unit
                  ? { value: series.unit, angle: -90, position: 'insideLeft', fill: colors.axis, fontSize: 12, dy: 24 }
                  : undefined
              }
            />
            <Tooltip
              content={renderTooltip}
              cursor={{ stroke: colors.axis, strokeDasharray: '3 3' }}
              isAnimationActive={!reducedMotion}
            />
            {probe && (
              <Area
                type="linear"
                dataKey="probeBand"
                stroke="none"
                fill={colors.series}
                fillOpacity={0.15}
                isAnimationActive={!reducedMotion}
                connectNulls
                data-testid="trend-probe-band"
              />
            )}
            {probe && (
              <Line
                type="linear"
                dataKey="probe"
                name={probe.label}
                stroke={colors.series}
                strokeOpacity={0.7}
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={false}
                activeDot={false}
                isAnimationActive={!reducedMotion}
                connectNulls
                data-testid="trend-probe-line"
              />
            )}
            <Line
              type="linear"
              dataKey="value"
              name={series.label}
              stroke={colors.series}
              strokeWidth={2}
              dot={{ r: 4, fill: colors.series, stroke: colors.surface, strokeWidth: 2 }}
              activeDot={{ r: 6, fill: colors.series, stroke: colors.surface, strokeWidth: 2 }}
              isAnimationActive={!reducedMotion}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
}

export default MeasurementTrendChart;
